import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Scale, FileText, ShieldAlert, BookOpen, Users, Gavel } from "lucide-react";

const SECTIONS = [
  { icon: BookOpen, title: "Supreme Court Filing Structure", body: "Caption, summons, verified complaint, affidavits, and exhibits — all structured to NY Supreme Court formatting expectations (DRL §170, §230, §240; CPLR §308, §306-b; CPLR Article 11)." },
  { icon: FileText, title: "Verified Complaint Drafting", body: "Numbered paragraphs covering residency, marriage facts, irretrievable breakdown grounds, no-reconciliation statement, and relief requested." },
  { icon: Users, title: "Custody & Support Considerations", body: "Best-interests-of-the-child framework (DRL §240), proposed custody arrangements, CSSA-aligned support language, and current living arrangements." },
  { icon: Gavel, title: "Service of Process & Affidavits", body: "Personal service, service by mail, service by publication — with timing requirements and proper affidavit-of-service templates." },
];

export default function DivorceOverview() {
  return (
    <div className="bg-black text-zinc-100">
      {/* Hero */}
      <section className="pt-16 pb-12 border-b border-zinc-900">
        <div className="container-pl max-w-3xl">
          <p className="overline mb-4">Attorney overview · Divorce (NY)</p>
          <h1 className="display-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-5 accent-rule">
            Divorce in New York — what to expect.
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed">
            An uncontested NY divorce is procedurally specific and unforgiving — small mistakes in caption, residency language, or service can stall the case for months. This page is a plain-English orientation. When you're ready for personalized help with documents, schedule a consultation.
          </p>
        </div>
      </section>

      {/* Overview sections */}
      <section className="py-14 border-b border-zinc-900">
        <div className="container-pl max-w-4xl">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="display-serif text-3xl text-zinc-50 accent-rule">What's involved.</h2>
            <p className="hidden sm:block text-xs text-zinc-500">Educational overview</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {SECTIONS.map((s) => (
              <div key={s.title} className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-6" data-testid={`divorce-section-${s.title.replace(/\s+/g,"-").toLowerCase()}`}>
                <div className="h-10 w-10 rounded-sm border border-amber-400/40 bg-blue-950/30 flex items-center justify-center mb-4">
                  <s.icon className="h-5 w-5 text-amber-400" strokeWidth={1.5}/>
                </div>
                <h3 className="display-serif text-xl text-zinc-50 mb-2 leading-tight">{s.title}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why an attorney */}
      <section className="py-14 border-b border-zinc-900 bg-zinc-950/40">
        <div className="container-pl max-w-3xl">
          <p className="overline mb-3">A note on counsel</p>
          <h2 className="display-serif text-3xl text-zinc-50 mb-5 accent-rule">Divorce is rarely a one-size form.</h2>
          <p className="text-zinc-300 leading-relaxed mb-4">
            Even an uncontested NY divorce can be complicated by property division, child custody, support calculations, or service-of-process logistics. We don't sell a self-service divorce packet because we don't believe it serves most people well.
          </p>
          <p className="text-zinc-300 leading-relaxed">
            Instead, we offer a focused consultation: we listen to your situation, explain the procedure, and help you decide whether you need a referral to a matrimonial attorney, paralegal document preparation, or both. There's no judgment and no pressure.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container-pl max-w-3xl">
          <div className="border border-amber-400/30 bg-gradient-to-br from-blue-950/30 to-black/40 rounded-sm p-8 lg:p-10 text-center" data-testid="divorce-cta-panel">
            <Scale className="h-10 w-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5}/>
            <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 mb-3">Talk to us before you file.</h2>
            <p className="text-zinc-300 max-w-xl mx-auto leading-relaxed mb-7">
              Schedule a confidential consultation. We'll review your situation and outline the cleanest path forward — whether that's working with us, a matrimonial attorney, or both.
            </p>
            <Link to="/booking">
              <Button size="lg" className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white px-7" data-testid="divorce-schedule-cta">
                Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5}/>
              </Button>
            </Link>
            <p className="text-[11px] text-zinc-500 italic mt-6">
              <ShieldAlert className="inline h-3 w-3 mr-1 -mt-0.5"/> We are not attorneys and do not provide legal advice. All services are administrative document preparation only.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
